import { useEffect, useState } from "react";
import axios from "axios";
import { Send, Check, Upload } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ALL_SERVICES } from "@/data/urban";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUDGETS = ["₹1L – ₹3L", "₹3L – ₹5L", "₹5L – ₹10L", "₹10L – ₹15L", "₹15L – ₹20L", "₹20L+"];

export const InquiryForm = ({
  heading = "Get a Free Quote",
  subheading = "No obligation · Reply within 24 hrs",
  source = "generic",
  productName,
  productCode,
  productUrl,
  defaultService,
  defaultBudget,
  className = "",
  compact = false,
  dark = false,
  testId = "inquiry-form",
}) => {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", city: "", service: defaultService || "", budget: defaultBudget || "", message: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { if (defaultService) setForm((s) => ({ ...s, service: defaultService })); }, [defaultService]);
  useEffect(() => { if (defaultBudget) setForm((s) => ({ ...s, budget: defaultBudget })); }, [defaultBudget]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2) e.name = "Enter your name.";
    if (!/^[0-9+\-\s()]{7,20}$/.test(form.mobile)) e.mobile = "Enter a valid mobile.";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      // Strip empty strings so optional EmailStr etc. validate correctly on backend
      const payload = { source, product_name: productName, product_code: productCode, product_url: productUrl || (typeof window !== "undefined" ? window.location.pathname : undefined) };
      Object.entries(form).forEach(([k, v]) => { if (v && v.toString().trim()) payload[k] = v.toString().trim(); });
      await axios.post(`${API}/contact`, payload);
      setDone(true);
      setForm({ name: "", mobile: "", email: "", city: "", service: defaultService || "", budget: defaultBudget || "", message: "" });
    } catch (_) {
      setErrors({ server: "Could not submit. Please call +91 95999 20839." });
    } finally { setBusy(false); }
  };

  const fieldCls = dark
    ? "w-full h-12 rounded-lg bg-white/10 border border-white/25 px-4 text-white placeholder:text-white/60 focus:outline-none focus:border-white text-sm"
    : "w-full h-12 rounded-lg bg-white border border-hairline px-4 text-ink placeholder:text-ink-soft/60 focus:outline-none focus:border-ink text-sm";
  const wrap = dark ? "bg-black text-white border border-white/10" : "bg-white text-ink border border-hairline";

  if (done) {
    return (
      <div data-testid={`${testId}-success`} className={`rounded-2xl p-8 text-center ${wrap} ${className}`}>
        <div className={`mx-auto h-14 w-14 rounded-full ${dark ? "bg-white text-black" : "bg-black text-white"} flex items-center justify-center`}><Check className="h-7 w-7" /></div>
        <h3 className="mt-4 font-heading text-xl font-bold">Thanks — we've got your details!</h3>
        <p className={`mt-2 text-sm ${dark ? "text-white/70" : "text-ink-soft"}`}>Our consultant will call you within 24 hours.</p>
      </div>
    );
  }

  return (
    <form data-testid={testId} onSubmit={submit} noValidate className={`rounded-2xl p-6 sm:p-7 ${wrap} ${className}`}>
      <div className="font-heading text-xl font-bold">{heading}</div>
      <p className={`text-sm mt-1 mb-5 ${dark ? "text-white/60" : "text-ink-soft"}`}>{subheading}</p>
      {productName && (
        <div className={`mb-4 rounded-lg px-3 py-2 text-xs ${dark ? "bg-white/10 text-white/80" : "bg-canvas text-ink-soft"}`}>
          Enquiring about: <span className="font-semibold">{productName}</span>{productCode ? ` (${productCode})` : ""}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <input data-testid={`${testId}-name`} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Name*" className={fieldCls} />
          {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
        </div>
        <div>
          <input data-testid={`${testId}-mobile`} value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="Mobile*" className={fieldCls} />
          {errors.mobile && <p className="text-xs text-red-500 mt-1">{errors.mobile}</p>}
        </div>
        {!compact && (
          <>
            <input data-testid={`${testId}-email`} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" className={fieldCls} />
            <input data-testid={`${testId}-city`} value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="City" className={fieldCls} />
            <Select value={form.service} onValueChange={(v) => set("service", v)}>
              <SelectTrigger data-testid={`${testId}-service`} className={fieldCls + " justify-between"}><SelectValue placeholder="Service required" /></SelectTrigger>
              <SelectContent>{ALL_SERVICES.map((s) => <SelectItem key={s.key} value={s.title}>{s.title}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
              <SelectTrigger className={fieldCls + " justify-between"}><SelectValue placeholder="Approx. budget" /></SelectTrigger>
              <SelectContent>{BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </>
        )}
      </div>
      {!compact && (
        <textarea data-testid={`${testId}-message`} value={form.message} onChange={(e) => set("message", e.target.value)} rows={3} placeholder="Tell us about your project…" className={`mt-3 w-full rounded-lg px-4 py-3 text-sm ${dark ? "bg-white/10 border border-white/25 text-white placeholder:text-white/60" : "bg-white border border-hairline text-ink placeholder:text-ink-soft/60"} focus:outline-none focus:border-${dark ? "white" : "ink"}`} />
      )}
      {errors.server && <p data-testid={`${testId}-error`} className="text-sm text-red-500 mt-2">{errors.server}</p>}
      <button type="submit" data-testid={`${testId}-submit`} disabled={busy} className={`mt-4 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-lg font-bold text-sm uppercase tracking-wide transition-colors disabled:opacity-60 ${dark ? "bg-white text-black hover:bg-white/90" : "bg-black text-white hover:bg-ink"}`}>
        {busy ? "Sending…" : <>Get Free Quote <Send className="h-4 w-4" /></>}
      </button>
      <p className={`text-[10px] mt-2 text-center ${dark ? "text-white/50" : "text-ink-soft/70"}`}>By submitting you agree to receive a callback from Urban Renovations.</p>
    </form>
  );
};
